/**
 * VideoTile - Specialized tile for displaying videos (athlete view)
 *
 * Simplified version for athletes with:
 * - Thumbnail with play button overlay
 * - Status badge
 * - Date and size info
 * - Click to view
 */

import { useState, CSSProperties } from 'react';
import { Play, Clock, CheckCircle, Loader2, AlertCircle, LucideIcon } from 'lucide-react';
import { EntityTile, TileBadge } from '../EntityTile';

export interface VideoTileVideo {
  video_id: string;
  title: string;
  // Accept null too — backend Video records can carry description=null and
  // forcing every caller to coalesce was just producing TS2322 noise.
  description?: string | null;
  status: string;
  created_at: string;
  file_size_bytes?: number;
  tags?: string[];
  thumbnail_url?: string;
}

export interface VideoTileProps {
  video: VideoTileVideo;
  variant?: 'standard' | 'compact' | 'card';
  onClick?: () => void;
}

// Video status configuration
interface VideoStatusConfig {
  label: string;
  color: string;
  bg: string;
  icon: LucideIcon;
  variant: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
}

const STATUS_CONFIG: Record<string, VideoStatusConfig> = {
  PENDING: {
    label: 'Pending',
    color: '#92400e',
    bg: '#fef3c7',
    icon: Clock,
    variant: 'warning',
  },
  PROCESSING: {
    label: 'Processing',
    color: '#1e40af',
    bg: '#dbeafe',
    icon: Loader2,
    variant: 'info',
  },
  UPLOADED: {
    label: 'Uploaded',
    color: '#1e40af',
    bg: '#dbeafe',
    icon: CheckCircle,
    variant: 'info',
  },
  READY: {
    label: 'Ready',
    color: 'var(--gold-600)',
    bg: '#ffedd5',
    icon: CheckCircle,
    variant: 'warning',
  },
  REVIEWED: {
    label: 'Reviewed',
    color: '#166534',
    bg: '#dcfce7',
    icon: CheckCircle,
    variant: 'success',
  },
  FAILED: {
    label: 'Failed',
    color: '#991b1b',
    bg: '#fee2e2',
    icon: AlertCircle,
    variant: 'danger',
  },
};

// Thumbnail component with play overlay
function VideoThumbnail({
  thumbnailUrl,
  onClick,
  isNew,
}: {
  thumbnailUrl?: string;
  onClick?: () => void;
  isNew?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const thumbnailStyle: CSSProperties = {
    width: '100%',
    aspectRatio: '16/9',
    borderRadius: 'var(--radius-md)',
    background: thumbnailUrl
      ? `url(${thumbnailUrl}) center/cover`
      : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: onClick ? 'pointer' : 'default',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 0.2s ease',
    transform: isHovered ? 'scale(1.02)' : 'scale(1)',
  };

  const playButtonStyle: CSSProperties = {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    transition: 'transform 0.2s ease',
    transform: isHovered ? 'scale(1.1)' : 'scale(1)',
  };

  return (
    <div
      style={thumbnailStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <div style={playButtonStyle}>
        <Play size={22} fill="#1e293b" style={{ color: '#1e293b', marginLeft: 2 }} />
      </div>
      {isNew && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            padding: '3px 8px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #000 100%)',
            color: 'white',
            fontSize: '10px',
            fontWeight: 700,
            borderRadius: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          NEW
        </div>
      )}
    </div>
  );
}

export function VideoTile({ video, variant = 'standard', onClick }: VideoTileProps) {
  const statusConfig = STATUS_CONFIG[video.status] || STATUS_CONFIG.PENDING;
  const StatusIcon = statusConfig.icon;

  // Check if video is new (within 24 hours)
  const isNew = new Date(video.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000);

  // Format file size
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return null;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  // Build badges
  const badges: TileBadge[] = [
    {
      label: statusConfig.label,
      variant: statusConfig.variant,
      icon: StatusIcon,
    },
  ];

  if (isNew) {
    badges.unshift({
      label: 'NEW',
      variant: 'info',
    });
  }

  // Build subtitle parts
  const subtitleParts: string[] = [];
  subtitleParts.push(new Date(video.created_at).toLocaleDateString());
  const fileSize = formatFileSize(video.file_size_bytes);
  if (fileSize) {
    subtitleParts.push(fileSize);
  }

  // Tags for footer
  const tagsFooter =
    video.tags && video.tags.length > 0 ? (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: 4 }}>
        {video.tags.slice(0, 4).map((tag, i) => (
          <span
            key={i}
            style={{
              padding: '2px 8px',
              background: 'var(--gray-100)',
              color: 'var(--gray-600)',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 500,
            }}
          >
            {tag}
          </span>
        ))}
        {video.tags.length > 4 && (
          <span style={{ fontSize: '11px', color: 'var(--gray-400)' }}>
            +{video.tags.length - 4}
          </span>
        )}
      </div>
    ) : null;

  // For card variant, render with thumbnail
  if (variant === 'card') {
    return (
      <EntityTile
        title={video.title}
        subtitle={subtitleParts.join(' · ')}
        badges={badges}
        variant="card"
        elevated
        onClick={onClick}
        thumbnail={<VideoThumbnail thumbnailUrl={video.thumbnail_url} isNew={isNew} />}
        footer={tagsFooter}
      />
    );
  }

  // Standard/compact variant with inline thumbnail
  return (
    <EntityTile
      title={video.title}
      subtitle={subtitleParts.join(' · ')}
      badges={badges}
      variant={variant}
      elevated
      onClick={onClick}
      avatar={
        <div
          style={{
            width: variant === 'compact' ? 48 : 64,
            height: variant === 'compact' ? 32 : 40,
            borderRadius: 'var(--radius-sm)',
            background: video.thumbnail_url
              ? `url(${video.thumbnail_url}) center/cover`
              : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Play size={variant === 'compact' ? 14 : 18} fill="white" style={{ color: 'white' }} />
        </div>
      }
      footer={tagsFooter}
    />
  );
}

export default VideoTile;
